import React, { FunctionComponent, memo, ReactNode, useCallback } from 'react'
import styles from './ProjectInfo.module.sass'
import { parseDate, toReadableDateString } from '../../../../../utils/date-utils'
import { Project, ProjectStatus, projectStatusToColor, projectStatusToName } from '../../../../../responses/projects'
import clsx from 'clsx'
import Button, { ButtonStyle } from '../../../../controls/Button/Button'
import Ellipsis from '../../../../etc/Ellipsis/Ellipsis'
import { useBoolean, useRootDispatch } from '../../../../../utils/hooks'
import { PERMISSIONS } from '../../../../../constants/permissions'
import { projectActions } from '../../../../../redux/data/project/project-actions'
import { push } from 'connected-react-router'
import { urlWithParams } from '../../../../../http/utils'
import { ROUTES } from '../../../../../http/routes'
import { infoToast } from '../../../../controls/Toaster/Toaster'
import copyToClipboard from 'copy-to-clipboard'
import Tooltip from 'react-tooltip-lite'
import { pipe } from 'fp-ts/es6/pipeable'
import BindIcon from 'files/bind.svg'
import EditIcon from 'files/edit.svg'
import CopyIcon from 'files/copy.svg'
import FinplanIcon from 'files/finplan.svg'
//import DeleteIcon from 'files/delete.svg'
import ProjectCloseIcon from 'files/project-close.svg'
import OpenIcon from 'files/task-open.svg'
import CloseIcon from 'files/task-closed.svg'
import QuestionIcon from 'files/question.svg'
import ArchiveIcon from 'files/archive/archive.svg'
import UnarchiveIcon from 'files/archive/unarchive.svg'
import Spacer from '../../../../etc/Spacer'
import { useQueries } from '../../../../../utils/hooks/useQueries'
import { Classes, Colors } from '../../../../../styles'
import { enumIs, enumMatchMultiple } from '../../../../../utils/enum-utils'
import { setLightness } from 'polished'
import Brief from '../Brief/Brief'
import { FileData } from '../../../../../responses/files'
import { galleryActions } from '../../../../../redux/global/gallery/gallery-actions'
import { inactiveContext } from 'ts/constants/contexts'
import { archiveActions } from '../../../../../redux/pages/archive'
import { HasAllPermissions, HasAnyPermission } from '../../../../etc/HasPermission'
import ConditionLink from '../../../../etc/ConditionLink'

const getTitle = enumMatchMultiple<ProjectStatus>()<string | undefined>(
    undefined,
    [[ProjectStatus.NEW], 'Проект нельзя закрыть, но можно отменить/удалить'],
    [[ProjectStatus.CANCELED, ProjectStatus.CLOSED, ProjectStatus.TO_ARCHIVE], 'Для открытия проекта совершите какое-либо действие в нём'],
)

const getColor = enumMatchMultiple<ProjectStatus>()(
    Colors.blue,
    [[ProjectStatus.NEW], Colors.orange],
    [[ProjectStatus.ARCHIVED], Colors.purpleMedium],
    [[ProjectStatus.CLOSED, ProjectStatus.CANCELED, ProjectStatus.TO_ARCHIVE], Colors.green],
)

interface ButtonData {
    text: string,
    icon: ReactNode
}

const getButtonData = enumMatchMultiple<ProjectStatus>()<{
    default: ButtonData,
    hover: ButtonData,
}>(
    {
        default: {
            text: 'Открыт',
            icon: <OpenIcon/>,
        },
        hover: {
            text: 'Закрыть',
            icon: <CloseIcon/>,
        },
    },
    [[ProjectStatus.CLOSED, ProjectStatus.CANCELED, ProjectStatus.TO_ARCHIVE], {
        default: {
            text: 'Закрыт',
            icon: <CloseIcon/>,
        },
        hover: {
            text: 'Открыть',
            icon: <OpenIcon/>,
        },
    }],
    [[ProjectStatus.ARCHIVED], {
        default: {
            text: 'Архив',
            icon: <ArchiveIcon/>,
        },
        hover: {
            text: 'Разархивировать',
            icon: <UnarchiveIcon/>,
        },
    }]
)

interface Props {
    className?: string,
    project: Project,
    minimal?: boolean,
    hideBrief?: boolean,
    children?: ReactNode,
}

const isFinished = enumIs<ProjectStatus>()([ProjectStatus.CLOSED, ProjectStatus.CANCELED, ProjectStatus.TO_ARCHIVE])

const ProjectInfo: FunctionComponent<Props> = props => {
    const dispatch = useRootDispatch()
    const {state: statusHover, setTrue, setFalse} = useBoolean()

    const buttonData = getButtonData(props.project.status_id)
    const finished = isFinished(props.project.status_id)
    const {isMobile} = useQueries()

    const preview = useCallback((current: FileData) => dispatch(galleryActions.open({
        type: 'project',
        projectId: props.project.id,
        current,
    })), [dispatch, props.project.id])


    return <div
        className={styles.container}
        style={{backgroundColor: setLightness(0.95, projectStatusToColor(props.project.status_id))}}
    >
        <div className={clsx(props.className, styles.info, {
            [styles.minimal]: props.minimal && props.project.dir_exists !== 0 && props.project.has_client !== 0,
            [styles.error]: props.project.dir_exists === 0 || props.project.has_client === 0
        })}>
            {
                props.project.dir_exists === 0 && props.project.has_client !== 0 && <div className={clsx(styles.error, Classes.cRed, Classes.f13)}>
                    Папка проекта отсутствует на диске. Удалите проект, либо верните папку в исходное место и проверьте наличие через систему
                </div>
            }
            {
                props.project.has_client === 0 && <div className={clsx(styles.error, Classes.cRed, Classes.f13)}>
                    Некорректный клиент. Возможно, он был удален или отредактирован.
                </div>
            }
            <div className={styles.title}>
                <div className={styles.top}>
                    <span>Проект&nbsp;|&nbsp;</span>
                    <span style={{color: projectStatusToColor(props.project.status_id)}}>
                        {projectStatusToName(props.project.status_id)}
                    </span>
                    <Spacer/>
                </div>
                <ConditionLink className={styles.center} condition={props.minimal} to={urlWithParams({projectId:props.project.id})(ROUTES.PROJECT_DETAIL)}>
                    <Ellipsis className={props.project.has_client === 0 ? Classes.cRed : ''}>{props.project.name}</Ellipsis>
                </ConditionLink>
                <div className={styles.bottom}>
                    <Tooltip useDefaultStyles className={styles.interactive} tagName={'span'} content={'Дата открытия'}>
                        {pipe(props.project.opened_at, parseDate, toReadableDateString)}
                    </Tooltip>
                    {
                        props.project.closed_at && <Tooltip useDefaultStyles className={styles.interactive} tagName={'span'} content={'Дата закрытия'}>
                            &emsp;{pipe(props.project.closed_at, parseDate, toReadableDateString)}
                        </Tooltip>
                    }
                    &emsp;
                    {
                        !isMobile && <>
                            <span className={Classes.cGrey}>Клиент:&nbsp;</span>
                            <span>{props.project.client?.short_title}</span>
                        </>
                    }
                </div>
            </div>
            {
                !isMobile && <>
                    <inactiveContext.Provider value={false}>
                        <Button
                            className={styles.copy}
                            title={props.project.folder}
                            onClick={() => {
                                copyToClipboard(props.project.folder)
                                infoToast('Путь скопирован в буфер обмена')
                            }}
                        >
                            <CopyIcon/> Копировать путь
                        </Button>
                    </inactiveContext.Provider>
                    {
                        props.project.dir_exists === 0 || (props.project.has_client === 0 && props.project.client !== null) ?
                            <inactiveContext.Provider value={false}>
                                {
                                    props.project.has_client !== 0 ? <Button
                                        className={styles.check}
                                        buttonStyle={ButtonStyle.BORDERED}
                                        title={'Проверить наличие каталога на диске'}
                                        color={Colors.blue}
                                        onClick={() => dispatch(projectActions.checkSource.request(props.project.id))}
                                    >
                                        <QuestionIcon/>
                                        Проверить наличие
                                    </Button> : ''
                                }<>
                                {
                                    props.project.has_client === 0 && props.project.client !== null ?
                                        <div className={styles.delete}>
                                            <HasAllPermissions permissions={[PERMISSIONS.PROJECT.EDIT]}>
                                                <Button
                                                    className={styles.edit}
                                                    title={'Редактировать проект'}
                                                    onClick={() => dispatch(push(urlWithParams({projectId: props.project.id})(ROUTES.PROJECTS_EDIT)))}
                                                >
                                                    <EditIcon/>
                                                </Button>
                                            </HasAllPermissions>
                                            <HasAnyPermission permissions={[PERMISSIONS.PROJECT.DELETE]}>
                                                {/*<Button
                                                    className={styles.cancel}
                                                    color={Colors.red}
                                                    title={'Удалить проект'}
                                                    onClick={() => dispatch(projectActions.startDelete(props.project))}
                                                >
                                                    <DeleteIcon/>
                                                </Button>*/}
                                            </HasAnyPermission>
                                        </div>: <HasAnyPermission permissions={[PERMISSIONS.PROJECT.DELETE]}>
                                            {/*<Button
                                                className={styles.delete}
                                                color={Colors.red}
                                                title={'Удалить проект'}
                                                onClick={() => dispatch(projectActions.startDelete(props.project))}
                                            >
                                                <DeleteIcon/>
                                            </Button>*/}
                                        </HasAnyPermission>
                                }

                            </>
                            </inactiveContext.Provider>: <>
                                {
                                    !props.minimal && <>
                                        <div className={styles.actions}>
                                            <HasAllPermissions permissions={[PERMISSIONS.PROJECT.EDIT]}>
                                                <div className={styles.objects}>{props.children}</div>
                                                {
                                                    !props.minimal && <>
                                                        <Button
                                                            title={'Связать проект'}
                                                            onClick={() => dispatch(projectActions.startBind(props.project))}
                                                        >
                                                            <BindIcon/>
                                                        </Button>
                                                        {(props.project.finplan ?
                                                                <a href={props.project.finplan.link} target={'_blank'}
                                                                   rel={'noreferrer'}>
                                                                    <Button
                                                                        title={'Перейти к проекту Finplan'}
                                                                        color={Colors.blue}
                                                                    >
                                                                        <FinplanIcon/>
                                                                    </Button>
                                                                </a> :
                                                                <Button
                                                                    title={'Создать проект Finplan'}
                                                                    onClick={() => dispatch(projectActions.createFinplan.request(props.project.id))}
                                                                >
                                                                    <FinplanIcon/>
                                                                </Button>
                                                        )}
                                                    </>
                                                }
                                            </HasAllPermissions>
                                            <HasAnyPermission
                                                condition={!props.minimal && (props.project.work_objects_count === 0 || !props.project.work_type)}
                                                permissions={[PERMISSIONS.PROJECT.DELETE]}
                                            >
                                               {/* <Button
                                                    color={Colors.red}
                                                    title={'Удалить проект'}
                                                    onClick={() => dispatch(projectActions.startDelete(props.project))}
                                                >
                                                    <DeleteIcon/>
                                                </Button>*/}
                                            </HasAnyPermission>
                                        </div>
                                        <inactiveContext.Provider value={false}>
                                            <HasAllPermissions
                                                condition={!props.project.imported && props.project.status_id !== ProjectStatus.RESERVED}
                                                permissions={[PERMISSIONS.PROJECT.CLOSE]}
                                            >
                                                <Button
                                                    className={styles.status}
                                                    title={getTitle(props.project.status_id)}
                                                    disabled={!!getTitle(props.project.status_id)}
                                                    buttonStyle={ButtonStyle.BORDERED}
                                                    color={getColor(props.project.status_id)}
                                                    onClick={() => dispatch(props.project.status_id === ProjectStatus.ARCHIVED?
                                                        archiveActions.startUnarchiving(props.project):
                                                        projectActions.startClose(props.project)
                                                    )}
                                                    onMouseEnter={setTrue}
                                                    onMouseLeave={setFalse}
                                                >
                                                    {statusHover? buttonData.hover.icon: buttonData.default.icon}
                                                    <Ellipsis>{statusHover? buttonData.hover.text: buttonData.default.text}</Ellipsis>
                                                </Button>
                                            </HasAllPermissions>
                                        </inactiveContext.Provider>
                                        <HasAllPermissions condition={!finished} permissions={[PERMISSIONS.PROJECT.CANCEL]}>
                                            <Button
                                                className={styles.cancel}
                                                title={'Отменить проект'}
                                                onClick={() => dispatch(projectActions.startCancel(props.project))}
                                            >
                                                <ProjectCloseIcon/>
                                            </Button>
                                        </HasAllPermissions>
                                    </>
                                }
                                <HasAllPermissions permissions={[PERMISSIONS.PROJECT.EDIT]}>
                                    <Button
                                        className={styles.edit}
                                        title={'Редактировать проект'}
                                        onClick={() => dispatch(push(urlWithParams({projectId: props.project.id})(ROUTES.PROJECTS_EDIT)))}
                                    >
                                        <EditIcon/>
                                    </Button>
                                </HasAllPermissions>
                            </>
                    }
                </>
            }
        </div>
        {
            (!isMobile && !props.hideBrief) && <Brief
                className={styles.brief}
                text={'Бриф проекта'}
                description={props.project.description}
                files={props.project.files}
                onFileClick={preview}
                projectInfo={true} // Фикс ошибки высоты изображений брифа
            />
        }
    </div>
}

export default memo(ProjectInfo)